import { getItineraryPublicPath } from 'utils/itineraryUrl'
import * as P from 'pages/Profile/modernProfile'

const normalizeCityName = (name: unknown) =>
  String(name ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')

const countDestinations = (itinerary: any) => {
  const destinations = new Set<string>()

  ;(itinerary?.cities ?? []).forEach((city: any) => {
    const cityName = normalizeCityName(city?.name)

    if (cityName) {
      destinations.add(cityName)
      return
    }

    const latitude = Number(city?.latitude)
    const longitude = Number(city?.longitude)

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      destinations.add(`${latitude.toFixed(4)}|${longitude.toFixed(4)}`)
    }
  })

  return destinations.size
}

const averageRating = (rates: any[] = []) => {
  const validRates = rates
    .map((item) => Number(item?.rate))
    .filter((rate) => Number.isFinite(rate))

  if (validRates.length === 0) return null
  return validRates.reduce((total, rate) => total + rate, 0) / validRates.length
}

const cleanTitle = (title: string) =>
  String(title ?? '')
    .replace(/\s*[—-]\s*Homologação\s*$/i, '')
    .trim()

interface Props {
  itinerary: any
  canManage?: boolean
  canDelete?: boolean
  onDelete?: (itinerary: any) => void
}

const ProfileItineraryCard = ({
  itinerary,
  canManage,
  canDelete,
  onDelete
}: Props) => {
  const rating = averageRating(itinerary?.rates ?? [])
  const destinations = countDestinations(itinerary)
  const days = Number(itinerary?.days ?? 0)
  const ratingsCount = itinerary?.rates?.length ?? 0

  return (
    <P.CardShell>
      {itinerary?.inspiredFromItineraryId && (
        <P.InspiredBadge
          to={getItineraryPublicPath({ id: itinerary.inspiredFromItineraryId })}
          title="Abrir o roteiro original"
        >
          Roteiro Inspirado
        </P.InspiredBadge>
      )}
      {itinerary?.isEditorial && !itinerary?.inspiredFromItineraryId ? (
        <P.CollaborativeBadge>Editorial Redsterna</P.CollaborativeBadge>
      ) : itinerary?.isCollaborative && !itinerary?.inspiredFromItineraryId ? (
        <P.CollaborativeBadge>Roteiro colaborativo</P.CollaborativeBadge>
      ) : null}
      {canManage && (
        <P.CardActions>
          <P.EditCardButton to={`/editar-roteiro/${itinerary.id}`}>
            Editar
          </P.EditCardButton>
          {canDelete && (
            <P.DeleteCardButton
              type="button"
              onClick={() => onDelete?.(itinerary)}
            >
              Excluir
            </P.DeleteCardButton>
          )}
        </P.CardActions>
      )}

      <P.Card
        to={{
          pathname: getItineraryPublicPath(itinerary),
          state: { itinerary }
        }}
      >
        <P.CardImage
          src={
            itinerary.coverImage ||
            '/images/desktop/home/redsterna_gray_logo.png'
          }
          alt={itinerary.title}
        />
        <P.CardBody>
          <P.CardTitle>{cleanTitle(itinerary.title)}</P.CardTitle>
          <P.RatingRow>
            {rating !== null ? (
              <>
                <P.RatingStar>★</P.RatingStar>
                <strong>{rating.toFixed(1).replace('.', ',')}</strong>
                <span>({ratingsCount})</span>
              </>
            ) : (
              <span>Sem avaliação</span>
            )}
          </P.RatingRow>
          <P.MetaRow>
            {days > 0 && <P.MetaItem>🗓 {days} dias</P.MetaItem>}
            <P.MetaItem>📍 {destinations} destinos</P.MetaItem>
          </P.MetaRow>
        </P.CardBody>
      </P.Card>
    </P.CardShell>
  )
}

export default ProfileItineraryCard
